import { Directive, ElementRef, NgZone, OnDestroy, OnInit, inject } from '@angular/core';

import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { CloseOnScroll } from './close-on-scroll';

/**
 * Dismisses an ngb click-popover as soon as the user scrolls.
 *
 * The note / custom-field / message popovers all use `container="body"`, so they
 * float in a fixed position and do not track their trigger when the page scrolls —
 * leaving them stranded over unrelated content. Closing on the first scroll keeps
 * things tidy.
 *
 * Auto-applies to every `[ngbPopover]` in the modules that import it. Listeners are
 * attached only while a popover is open (on `shown`) and removed on `hidden`; see
 * {@link CloseOnScroll} for the scroll sources covered.
 */
@Directive({
  selector: '[ngbPopover]',
  standalone: true,
})
export class ClosePopoverOnScrollDirective implements OnInit, OnDestroy {
  private readonly popover = inject(NgbPopover);

  private readonly subscriptions = new Subscription();
  private readonly closer = new CloseOnScroll(
    inject<ElementRef<HTMLElement>>(ElementRef).nativeElement,
    inject(NgZone),
    () => this.popover.isOpen(),
    () => {
      this.popover.close();
    },
  );

  ngOnInit(): void {
    this.subscriptions.add(this.popover.shown.subscribe(() => {
      this.closer.attach();
    }));
    this.subscriptions.add(this.popover.hidden.subscribe(() => {
      this.closer.detach();
    }));
  }

  ngOnDestroy(): void {
    this.closer.detach();
    this.subscriptions.unsubscribe();
  }
}
